import {
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { ApplicationView } from "@mixitone/mvc";
import { clsx } from "clsx";
import React, { type PropsWithChildren, useCallback } from "react";
import { Dropdown, DropdownController } from "./Dropdown";

interface MenuItemProps<T = undefined> {
  link?: React.ReactElement<{ to: string; className?: string }>;
  href?: string;
  onClick?: (value: T) => void;
  closeOnClick?: boolean;
  className?: string;
  value?: T;
}

function DropdownMenuItemComponent<T>(props: PropsWithChildren<MenuItemProps<T>>) {
  const { value, children, href, onClick, closeOnClick, className, link } = props;
  const controller = DropdownController.use();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        event.preventDefault();
        // Value can be forced because we allow undefined
        onClick(value!);
        if (closeOnClick) controller.actionClose();
      } else {
        controller.actionClose();
      }
    },
    [onClick, controller, value, closeOnClick],
  );

  if (link) {
    return React.cloneElement(
      link,
      {
        className: clsx(
          "hover:bg-brand-100 block whitespace-nowrap px-4 py-2 text-gray-800 hover:text-gray-800",
          link.props.className,
        ),
      },
      children,
    );
  }

  if (href || onClick) {
    return (
      <a
        href={href || "#"}
        onClick={handleClick}
        className={clsx(
          "hover:bg-brand-100 block whitespace-nowrap px-4 py-2 text-gray-800 hover:text-gray-800",
          className,
        )}
        role="menuitem"
      >
        {children}
      </a>
    );
  }

  return (
    <div className={clsx("block px-4 py-2 text-gray-800", className)} role="menuitem">
      {children}
    </div>
  );
}

interface SubmenuProps {
  label: React.ReactNode;
  className?: string;
}

const DropdownSubmenuComponent: React.FC<PropsWithChildren<SubmenuProps>> = ({
  label,
  children,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { refs, context, floatingStyles } = useFloating({
    placement: "right-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset({ crossAxis: -10, mainAxis: 0 }), flip(), shift()],
  });
  const hover = useHover(context, { handleClose: safePolygon() });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  return (
    <div
      className={clsx(
        "hover:bg-brand-100 whitespace-nowrap px-4 py-2 text-gray-800 hover:text-gray-800",
        className,
      )}
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      <div className="flex items-center justify-between gap-5">
        <span>{label}</span>
        <span className="scale-75">▶</span>
      </div>
      <DropdownMenu
        open={isOpen}
        onClose={() => setIsOpen(false)}
        dropRef={refs.setFloating}
        floatingProps={getFloatingProps()}
        style={floatingStyles}
        portal={false}
      >
        {children}
      </DropdownMenu>
    </div>
  );
};

const DropdownMenu = Dropdown;
const DropdownMenuItem: typeof DropdownMenuItemComponent = ApplicationView(
  DropdownMenuItemComponent,
) as typeof DropdownMenuItemComponent;
const DropdownSubmenu = ApplicationView(DropdownSubmenuComponent);
const DropdownMenuSeparator = () => <div className="border-t border-gray-200" />;

export { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator, DropdownSubmenu };
